import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowLeft, Check, CircleCheck, Copy, Info, MessageCircleQuestion, RefreshCw, RotateCcw, Wand2 } from 'lucide-react';

import { DIET_PLAN_POINTS } from '../../graphql/mutations';
import { aiLimitOf, aiLimitText } from '../../utils/aiLimits';
import { trackEvent } from '../../utils/gt';
import { numberFormatter } from '../../utils/calorieLog';
import { showUndoToast } from '../../utils/undoToast';
import { applyAction, differsFromOriginal, planActions, planNumbers } from '../../utils/dietAnalysis';
import '../../styles/pages/diet-import.css';

// The points already written for a plan's numbers, kept while the app is open: reopening doesn't ask the AI again
const pointsCache = new Map();

const FIBER_REFERENCE = 25;

/**
 * "Pontos para a consulta": the plan measured here, without AI (calories against the questionnaire's goal when there
 * is one, protein, fiber, the meals' share of the day, variety), and points worded by the AI from those numbers only,
 * as things to take to the nutritionist, never corrections. Nothing changes by itself: the only one-tap changes come
 * from the plan's own options and substitutions, each with an undo, and the nutritionist's plan is a tap away.
 *
 * Without the questionnaire there's no calorie goal to compare with (it needs sex, age, height, weight and activity):
 * the analysis compares the plan with itself, and an optional weight, kept on this screen only, gives protein per kg.
 */
export const DietPoints = ({ planId, weekPlan, origin, originalWeekPlan, substitutions, questionnaire, onBack, onChangeWeek }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ix = (key, options) => t(`pages.DietImport.${key}`, options);
  const diet = (key, options) => t(`pages.DietPlan.${key}`, options);
  const formatNumber = numberFormatter(language);
  const locale = language === 'pt' ? 'pt-BR' : 'en-US';
  const [askPoints] = useMutation(DIET_PLAN_POINTS);

  const [typedWeight, setTypedWeight] = useState('');
  const [points, setPoints] = useState(null);
  const [state, setState] = useState('loading');
  // A daily-limit refusal (10 a day): said as such, instead of the generic error
  const [limit, setLimit] = useState(null);
  const [sentKey, setSentKey] = useState(null);
  const [applied, setApplied] = useState([]);
  const weekRef = useRef(weekPlan);
  weekRef.current = weekPlan;

  const weightKg = Number(String(typedWeight).replace(',', '.')) || null;
  const signed = (value, unit) => `${value > 0 ? '+' : value < 0 ? '−' : '±'}${formatNumber(Math.abs(value))} ${unit}`;
  const labelOf = (action) => {
    const meal = action.mealName;
    if (action.kind === 'option') return ix('actionOption', { meal, n: action.option + 1 });
    if (action.kind === 'alternate') return ix('actionAlternate', { meal });
    return ix('actionSubstitution', { food: action.foodName, item: action.item?.name, meal });
  };
  const effectOf = (action) => ix('actionEffect', { kcal: signed(action.delta.calories, 'kcal'), protein: signed(action.delta.protein, 'g') });

  const numbers = useMemo(
    () => planNumbers({ weekPlan, origin, questionnaire, weightKg: weightKg >= 30 && weightKg <= 300 ? weightKg : null, substitutions, labelOf, effectOf }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekPlan, origin, questionnaire, weightKg, substitutions, language]
  );
  const actions = useMemo(() => planActions(weekPlan, substitutions), [weekPlan, substitutions]);
  const key = `${planId}:${JSON.stringify({ ...numbers, actions: undefined })}`;

  const fetchPoints = async (requestKey, requestNumbers) => {
    setState('loading');
    setSentKey(requestKey);
    try {
      const { data } = await askPoints({ variables: { numbers: requestNumbers, lang: language, healthDataConsent: true } });
      const list = data?.dietPlanPoints?.points;
      if (!Array.isArray(list) || !list.length) throw new Error('No points');
      pointsCache.set(requestKey, list);
      setPoints(list);
      setState('ready');
    } catch (error) {
      const refused = aiLimitOf(error);
      setLimit(refused ? aiLimitText(t, refused, { locale: language }) : null);
      setState('error');
    }
  };

  useEffect(() => {
    trackEvent('diet_plan_points_open', 'Diet Plan - Points Open', 'diet_plan', true);
    if (!numbers) return;
    const cached = pointsCache.get(key);
    if (cached) {
      setPoints(cached);
      setSentKey(key);
      setState('ready');
      return;
    }
    fetchPoints(key, numbers);
    // Asked once when the view opens; a change to the numbers offers "Atualizar os pontos"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!numbers) return null;

  const apply = (actionId) => {
    const action = actions.find((candidate) => candidate.id === actionId);
    if (!action) return;
    const previous = weekRef.current;
    const next = applyAction(previous, action);
    trackEvent('diet_plan_points_apply', `Diet Plan - Points Apply: ${action.kind}`, 'diet_plan', true);
    onChangeWeek(next);
    setApplied((list) => [...list, { id: action.id, label: labelOf(action) }]);
    showUndoToast(ix('appliedToast'), {
      undoLabel: diet('ui.undo'),
      onUndo: () => {
        onChangeWeek(previous);
        setApplied((list) => list.filter((item) => item.id !== action.id));
      },
    });
  };

  const backToNutritionist = () => {
    trackEvent('diet_plan_points_restore', 'Diet Plan - Back to Nutritionist Plan', 'diet_plan', true);
    const previous = weekRef.current;
    onChangeWeek(originalWeekPlan);
    setApplied([]);
    showUndoToast(ix('restoredToast'), { undoLabel: diet('ui.undo'), onUndo: () => onChangeWeek(previous) });
  };

  const asks = (points || []).filter((point) => point.kind !== 'fine');
  const fines = (points || []).filter((point) => point.kind === 'fine');

  const copyList = async () => {
    trackEvent('diet_plan_points_copy', 'Diet Plan - Points Copy', 'diet_plan', true);
    const lines = [ix('copyHeader'), ...asks.map((point, index) => `${index + 1}. ${point.title}${point.question ? ` — ${point.question}` : ''}`)];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success(ix('copiedToast'), { toastId: 'diet-points-copied' });
    } catch {
      toast.error(ix('copyError'), { toastId: 'diet-points-copy-error' });
    }
  };

  const { calories, protein, fiber, meals } = numbers;
  const goalGap = calories.goal ? calories.plan - calories.goal : null;
  const isNumbersChanged = state === 'ready' && sentKey !== key;
  const canRestore = origin === 'imported' && differsFromOriginal(weekPlan, originalWeekPlan);
  const firstTime = meals.find((meal) => meal.time)?.time;
  const lastTime = [...meals].reverse().find((meal) => meal.time)?.time;

  const bar = (share) => (
    <span className="mt-di-bar-track" aria-hidden="true">
      <span style={{ width: `${Math.min(Math.max(share, 0), 1) * 100}%` }} />
    </span>
  );

  return (
    <div className="mt-di mt-di-points">
      <div className="mt-di-bar">
        <button type="button" className="mt-di-bar__back" onClick={onBack}>
          <ArrowLeft aria-hidden="true" />
          <span>{diet('ui.title')}</span>
        </button>
      </div>
      <div className="mt-di-head">
        <div>
          <h1 className="mt-di-head__title">{ix('pointsTitle')}</h1>
          <p className="mt-di-head__sub">{ix(origin === 'imported' ? 'pointsSub' : 'pointsSubManual')}</p>
        </div>
        <div className="mt-di-head__actions">
          <button type="button" className="mt-btn mt-btn--quiet" onClick={copyList} disabled={!asks.length}>
            <Copy aria-hidden="true" />
            {ix('copyList')}
          </button>
        </div>
      </div>

      <div className="mt-di-review mt-di-review--build mt-di-points__layout">
        <div className="mt-di-review__side is-sticky">
          <section className="mt-di-card mt-di-numbers" aria-labelledby="mt-di-numbers-title">
            <h2 id="mt-di-numbers-title" className="mt-di-card-title">
              {ix('numbersTitle')}
            </h2>
            <p className="mt-di-numbers__lead">
              {calories.goal ? ix('numbersLeadGoal', { goal: diet(calories.goalKind === 'gain' ? 'gain' : calories.goalKind === 'maintain' ? 'maintain' : 'lose').toLowerCase() }) : ix('numbersLeadNoGoal')}
            </p>
            <dl className="mt-di-numbers__list">
              <div>
                <dt>{ix('caloriesLabel')}</dt>
                <dd>
                  <b>{formatNumber(calories.plan)} kcal</b>
                  {calories.goal ? ` · ${ix('goalKcal', { kcal: formatNumber(calories.goal) })}` : ` ${ix('perDay')}`}
                </dd>
                {calories.goal ? (
                  <>
                    {bar(calories.plan / calories.goal)}
                    <p className="mt-di-numbers__note">
                      {Math.abs(goalGap) < 50 ? ix('goalClose') : ix(goalGap < 0 ? 'goalBelow' : 'goalAbove', { kcal: formatNumber(Math.abs(goalGap)) })}
                    </p>
                  </>
                ) : (
                  calories.min !== calories.max && <p className="mt-di-numbers__note">{ix('kcalRange', { min: formatNumber(calories.min), max: formatNumber(calories.max) })}</p>
                )}
              </div>
              <div>
                <dt>{diet('protein')}</dt>
                <dd>
                  <b>{protein.grams} g</b> · {ix('shareKcal', { pct: protein.share })}
                </dd>
                {protein.perKg ? (
                  <p className="mt-di-numbers__note">{ix('perKg', { value: protein.perKg.toLocaleString(locale, { maximumFractionDigits: 1 }) })}</p>
                ) : (
                  <label className="mt-di-weight">
                    <span>{ix('weightAsk')}</span>
                    <input type="number" inputMode="decimal" min="30" max="300" value={typedWeight} onChange={(event) => setTypedWeight(event.target.value)} aria-label={ix('weightLabel')} />
                    <span aria-hidden="true">kg</span>
                  </label>
                )}
              </div>
              <div>
                <dt>{ix('fiber')}</dt>
                {fiber.grams == null ? (
                  <dd>{ix('fiberUnknown')}</dd>
                ) : (
                  <>
                    <dd>
                      <b>{fiber.grams} g</b> · {ix('fiberReference', { grams: FIBER_REFERENCE })}
                    </dd>
                    {bar(fiber.grams / FIBER_REFERENCE)}
                  </>
                )}
              </div>
              <div>
                <dt>{ix('mealsLabel')}</dt>
                <dd>
                  <b>{meals.length}</b>
                  {firstTime && lastTime && firstTime !== lastTime ? ` · ${ix('mealsSpan', { from: firstTime, to: lastTime })}` : ''}
                </dd>
                <ul className="mt-di-spread">
                  {meals.map((meal, index) => (
                    <li key={`${index}-${meal.name}`}>
                      <span className="mt-di-spread__name">{meal.name}</span>
                      <span className="mt-di-spread__pct">{meal.share}%</span>
                      {bar(meal.share / 40)}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <dt>{ix('variety')}</dt>
                <dd>
                  <b>{ix('distinctFoods', { count: numbers.variety.distinctFoods })}</b>
                  {numbers.variety.sameEveryDay ? ` · ${ix('sameMenu')}` : ''}
                </dd>
                {numbers.estimates > 0 && <p className="mt-di-numbers__note">{ix('estimatesNote', { count: numbers.estimates })}</p>}
              </div>
            </dl>
          </section>
        </div>

        <div className="mt-di-review__main">
          <p className="mt-di-info">
            <Info aria-hidden="true" />
            <span>
              <b>{ix(origin === 'imported' ? 'whoDecides' : 'whoDecidesManual')}</b> {ix(origin === 'imported' ? 'whoDecidesText' : 'whoDecidesTextManual')}
            </span>
          </p>

          {(applied.length > 0 || canRestore) && (
            <section className="mt-di-card mt-di-changes" aria-labelledby="mt-di-changes-title">
              <h2 id="mt-di-changes-title" className="mt-di-card-title">
                {ix('changesTitle')}
              </h2>
              {applied.length > 0 && (
                <ul className="mt-di-changes__list">
                  {applied.map((item) => (
                    <li key={item.id}>
                      <Check aria-hidden="true" />
                      {item.label}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-di-changes__text">{ix(origin === 'imported' ? 'changesText' : 'changesTextManual')}</p>
              {canRestore && (
                <button type="button" className="mt-btn mt-btn--quiet" onClick={backToNutritionist}>
                  <RotateCcw aria-hidden="true" />
                  {ix('restoreButton')}
                </button>
              )}
            </section>
          )}

          {isNumbersChanged && (
            <button type="button" className="mt-btn mt-btn--quiet mt-di-refresh" onClick={() => fetchPoints(key, numbers)}>
              <RefreshCw aria-hidden="true" />
              {ix('refreshPoints')}
            </button>
          )}

          {state === 'loading' && (
            <div className="mt-di-points__loading" role="status" aria-live="polite">
              <span className="mt-sr-only">{ix('pointsLoading')}</span>
              {[0, 1, 2].map((index) => (
                <div key={index} className="mt-di-card mt-di-point mt-di-point--skeleton" aria-hidden="true">
                  <span className="mt-skel" style={{ width: '30%', height: 12 }} />
                  <span className="mt-skel" style={{ width: '70%', height: 20 }} />
                  <span className="mt-skel" style={{ width: '100%', height: 14 }} />
                  <span className="mt-skel" style={{ width: '85%', height: 14 }} />
                </div>
              ))}
            </div>
          )}

          {state === 'error' && (
            <section className="mt-di-card mt-di-point">
              <p className="mt-di-point__title">{limit ? limit.title : ix('pointsErrorTitle')}</p>
              <p className="mt-di-point__body">{limit ? limit.body : ix('pointsErrorText')}</p>
              {(!limit || limit.canRetry) && (
                <button type="button" className="mt-btn mt-btn--quiet" onClick={() => fetchPoints(key, numbers)}>
                  <RefreshCw aria-hidden="true" />
                  {ix('tryAgain')}
                </button>
              )}
            </section>
          )}

          {state === 'ready' && (
            <>
              <ol className="mt-di-point-list">
                {asks.map((point, index) => {
                  const action = point.actionId && actions.find((candidate) => candidate.id === point.actionId);
                  const isApplied = applied.some((item) => item.id === point.actionId);
                  return (
                    <li key={`${index}-${point.title}`} className="mt-di-card mt-di-point">
                      {point.area && <p className="mt-di-point__area">{point.area}</p>}
                      <h2 className="mt-di-point__title">{point.title}</h2>
                      {point.body && <p className="mt-di-point__body">{point.body}</p>}
                      {point.question && (
                        <blockquote className="mt-di-point__ask">
                          <span>
                            <MessageCircleQuestion aria-hidden="true" />
                            {ix('askAtVisit')}
                          </span>
                          <q>{point.question}</q>
                        </blockquote>
                      )}
                      {(action || isApplied) && (
                        <div className="mt-di-point__action">
                          {isApplied ? (
                            <span className="mt-di-point__done">
                              <Check aria-hidden="true" />
                              {ix('appliedLabel')}
                            </span>
                          ) : (
                            <>
                              <button type="button" className="mt-btn mt-btn--quiet" onClick={() => apply(action.id)}>
                                <Wand2 aria-hidden="true" />
                                {labelOf(action)}
                              </button>
                              <span className="mt-di-point__effect">{ix('fromYourPlan', { effect: effectOf(action) })}</span>
                            </>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
              {fines.length > 0 && (
                <section className="mt-di-card mt-di-fine" aria-labelledby="mt-di-fine-title">
                  <h2 id="mt-di-fine-title" className="mt-di-card-title">
                    {ix('fineTitle')}
                  </h2>
                  <ul>
                    {fines.map((point, index) => (
                      <li key={`${index}-${point.title}`}>
                        <CircleCheck aria-hidden="true" />
                        <span>
                          <b>{point.title}</b> {point.body}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
